import { ActionTree } from 'vuex';
import { NewsletterState } from './types';
import { RootState } from '@/store/types';
import api from '@/api';
import * as listRequests from '@/api/requests/newsletter/mailup/lists';
import * as groupRequests from '@/api/requests/newsletter/mailup/groups';
import { GroupRecipientResource, ListResource } from '@/api/requests/newsletter/mailup/types';
import List from '@/api/models/newsletter/list';
import router from '@/router';
import Group from '@/api/models/newsletter/group';

export const actions: ActionTree<NewsletterState, RootState> = {
    /**
     * Create a group.
     *
     * @param commit
     * @param group
     */
    async createGroup({ commit }, group: Group) {
        if (!group.idList) {
            throw new Error('List ID is required to create a group.');
        }
        const response = await api.call(groupRequests.STORE(group.idList, group.toRequest()));
        const resource = response.data.data;
        commit('addGroup', resource);
        router.push({ name: 'newsletter.list.show', params: { listId: resource.idList.toString() } });
    },

    /**
     * Create a list.
     */
    async createList({ commit }, list: List) {
        const response = await api.call(listRequests.SAVE(list.toRequest()));
        const resource = response.data.data;
        commit('addList', resource);
        router.push({ name: 'newsletter.list.show', params: { listId: resource.idList.toString() } });
    },

    /**
     * Delete a group.
     *
     * @param commit
     * @param getters
     * @param groupId
     */
    async deleteGroup({ commit, getters }, groupId: number) {
        const group = getters.getGroupById(groupId);
        await api.call(groupRequests.DESTROY(group.idList, groupId));
        commit('removeGroup', groupId);
        commit('removeActiveGroupsRecipientsByGroupId', groupId);
    },

    /**
     * Delete a list.
     *
     * @param commit
     * @param dispatch
     * @param state
     * @param listId
     */
    async deleteList({ commit, dispatch, state }, listId: number) {
        await api.call(listRequests.DESTROY(listId));
        commit('removeList', listId);
        if (state.activeList && state.activeList.idList === listId) {
            dispatch('resetActiveList');
        }
    },

    /**
     * Fetch all lists.
     * @param commit
     */
    async fetchLists({ commit }) {
        const response = await api.call(listRequests.ALL());
        commit('setLists', response.data);
    },

    /**
     * Fetch list groups.
     * @param commit
     * @param state
     */
    async findActiveListGroups({ commit, state }) {
        if (!state.activeList) {
            return;
        }
        const response = await api.call(listRequests.GET_GROUPS(state.activeList.idList));
        commit('setActiveListGroups', response.data);
    },

    /**
     * Fetch list recipients.
     * @param commit
     * @param state
     */
    async findActiveListRecipients({ commit, state }) {
        if (!state.activeList) {
            return;
        }
        const response = await api.call(listRequests.GET_RECIPIENTS_SUBSCRIPTIONSTATUS(state.activeList.idList, 'emailoptins'));
        commit('setActiveListRecipients', response.data);
    },

    /**
     * Find a list by id.
     * @param commit
     * @param id
     */
    async findActiveList({ commit }, id: number) {
        const response = await api.call(listRequests.FIND(id));
        commit('setActiveList', response.data.data);
    },

    /**
     * Fetch list recipients.
     * @param commit
     * @param state
     * @param groupId
     */
    async findGroupRecipients({ commit, state }, groupId: number) {
        if (!state.activeList) {
            return;
        }
        const response = await api.call(groupRequests.GET_RECIPIENTS(groupId));
        const recipients = response.data.map((recipient: GroupRecipientResource) => ({...recipient, idGroup: groupId}));
        commit('removeActiveGroupsRecipientsByGroupId', groupId);
        commit('addActiveGroupsRecipients', recipients);
    },

    /**
     * Init lists.
     * @param commit
     * @param dispatch
     * @param getters
     */
    async initLists({ commit, dispatch, getters }) {
        try {
            if (!(getters.newsletterModuleEnabled && getters.mailupCredentialsSet)) {
                throw new Error('Newsletter module is not enabled, or credentials are not set.');
            }
            await dispatch('fetchLists');
            commit('setMailupLogged', true);
        } catch (e) {
            commit('setMailupLogged', false);
        }
    },

    /**
     * Init lists.
     * @param commit
     * @param dispatch
     * @param getters
     * @param listId
     */
    async initActiveList({ commit, dispatch, getters }, listId: number) {
        dispatch('resetActiveList');
        await dispatch('findActiveList', listId);
    },

    /**
     * Init lists.
     * @param commit
     * @param dispatch
     * @param getters
     * @param listId
     */
    async initActiveListItems({ commit, dispatch, getters }, listId: number) {
        commit('resetActiveListGroups');
        commit('resetActiveListRecipients');
        commit('resetActiveGroupsRecipients');
        await Promise.all([
            dispatch('findActiveListGroups'),
            dispatch('findActiveListRecipients'),
        ]);
    },

    /**
     * Reset active list.
     *
     * @param commit
     */
    resetActiveList({ commit }) {
        commit('resetActiveList');
        commit('resetActiveListGroups');
        commit('resetActiveListRecipients');
        commit('resetActiveGroupsRecipients');
    },

    /**
     * Update a group.
     *
     * @param commit
     * @param group
     */
    async updateGroup({ commit }, group: Group) {
        if (!group.idGroup || !group.idList) {
            throw new Error('List ID and Group ID are required to update a group.');
        }
        const response = await api.call(groupRequests.UPDATE(group.idList, group.idGroup, group.toRequest()));
        const resource = response.data.data;
        commit('updateGroup', resource);
        router.push({ name: 'newsletter.list.show', params: { listId: resource.idList.toString() } });
    },

    /**
     * Update a list.
     *
     * @param commit
     * @param state
     * @param list
     */
    async updateList({ commit, state }, list: List) {
        if (!list.idList) {
            throw new Error('List ID is required to update a list.');
        }
        const response = await api.call(listRequests.UPDATE(list.idList, list.toRequest()));
        const resource = response.data.data;
        commit('updateList', resource);
        if (state.activeList?.idList === resource.idList) {
            commit('setActiveList', resource);
        }
        router.push({ name: 'newsletter.list.show', params: { listId: resource.idList.toString() } });
    },
};
